import styles from "../UploadForm.module.css";

interface BbWcTogglePairProps {
  legend: string;
  fallbackLabel: string;
  alwaysLabel: string;
  fallback: boolean;
  always: boolean;
  disabled: boolean;
  onFallback: (value: boolean) => void;
  onAlways: (value: boolean) => void;
}

/**
 * One "fallback" / "always" checkbox pair, shared between the landing and
 * customs sections of {@link ./BbWcRates.view.tsx}.
 *
 * Built from native checkboxes inside `fieldset`/`legend` rather than the UI
 * package's Checkbox, because it exports none - following
 * `SourceToggle.view.tsx` as the precedent for the pattern.
 *
 * The nested "always" box is disabled until "fallback" is ticked, reflecting
 * the coupling that lives in the hook's setters (see `useBbWcToggle.ts`): this
 * component only renders the two booleans it is given, it does not enforce
 * the rule itself.
 *
 * @example
 * <BbWcTogglePair legend="Landing rate" fallbackLabel="Use a quoted rate..."
 *   alwaysLabel="Always use the quoted rate..." fallback={false} always={false}
 *   disabled={false} onFallback={setFallback} onAlways={setAlways} />
 */
export function BbWcTogglePair(props: BbWcTogglePairProps) {
  const { legend, fallbackLabel, alwaysLabel, fallback, always, disabled, onFallback, onAlways } =
    props;

  return (
    <fieldset className={styles.bbWcGroup} disabled={disabled}>
      <legend className={styles.bbWcLegend}>{legend}</legend>
      <label className={styles.bbWcOption}>
        <input
          type="checkbox"
          checked={fallback}
          disabled={disabled}
          onChange={(event) => onFallback(event.target.checked)}
        />
        <span>{fallbackLabel}</span>
      </label>
      <label className={`${styles.bbWcOption} ${styles.bbWcNested}`}>
        <input
          type="checkbox"
          checked={always}
          disabled={disabled || !fallback}
          onChange={(event) => onAlways(event.target.checked)}
        />
        <span>{alwaysLabel}</span>
      </label>
    </fieldset>
  );
}
